import type { PanelItem } from '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'
import cn from 'classnames'
import * as R from 'ramda'
import { useCallback, useEffect } from 'react'
import React from 'react'

import type { IRenderItemProps } from 'components/uiKit/ArrayControl'
import Button, { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import ScrollService, { scrollProps } from 'services/Scroll/ScrollService'
import { ScrollContainerEnum } from 'services/Scroll/enums'
import { setElement } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getActiveElementId, getElementState } from 'services/Store/Project/selectors'
import { t } from 'services/Translation'

import * as s from './SliderFormValueItem.module.scss'
import type { SliderStateType } from './sliderTypes'

export const SliderFormValueItem: React.FC<IRenderItemProps<PanelItem>> = ({
  index,
  item,
  drag,
  isDragging,
  onChange,
  value: array,
  min,
  max,
}) => {
  const dispatch = useProjectDispatch()
  const elementId = useProjectContext(getActiveElementId)
  const state = useProjectContext(getElementState, elementId || '')
  const isActive = item.value === state?.active
  const disableClone = Boolean(max && array.length >= max)
  const disableDelete = Boolean(min && array.length <= min)

  const setState = useCallback(
    (value: SliderStateType) => {
      if (elementId) {
        dispatch(setElement({ id: elementId, value }))
      }
    },
    [dispatch, elementId],
  )

  const handleBlur = useCallback(() => setState({ ...state, active: null }), [setState, state])

  const handleFocus = useCallback(
    () => setState({ ...state, active: item.value }),
    [setState, state, item.value],
  )

  const onClone = useCallback(() => {
    const clone = { ...R.clone(item), value: generateId() }
    return onChange?.([...array, clone], { command: 'clone', item, clone })
  }, [onChange, array, item])

  const onDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      return onChange?.(
        array.filter(({ value }) => value !== item.value),
        { command: 'delete', item },
      )
    },
    [onChange, array, item],
  )

  useEffect(() => {
    if (isActive) {
      ScrollService.scroll({ container: ScrollContainerEnum.toolbar, id: item.value }, null)
    }
  }, [isActive, item.value])

  return (
    <Button
      name={`slide-item-${index}`}
      styleType='free'
      {...scrollProps(item.value)}
      className={cn(s.root, { [s.active]: !isDragging && state?.active === item.value })}
      onBlur={handleBlur}
      onClick={handleFocus}
      ref={drag}
    >
      <span className={s.title}>
        <Icon name='builderDrugAndDrop' size={KitSize.S} />
        {t('elements.slider.form.slide')} {index + 1}
      </span>
      <span className={s.actions}>
        <Tooltip overlay={t('uiKit.tooltip.duplicate')}>
          <IconButton
            disabled={disableClone}
            icon='otherCopy'
            name='clone'
            onClick={onClone}
            size={KitSize.XS}
            styleType='ghost'
          />
        </Tooltip>
        <Tooltip overlay={t('uiKit.tooltip.delete')}>
          <IconButton
            disabled={disableDelete}
            icon='otherTrash'
            name='delete'
            onClick={onDelete}
            size={KitSize.XS}
            styleType='ghost'
          />
        </Tooltip>
      </span>
    </Button>
  )
}

export default React.memo(SliderFormValueItem) as typeof SliderFormValueItem
